import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { getPossibleDuplicates, setDuplicateState, scanDuplicates } from "../services/userService";
import { STATE_FILTERS, stateLabel, pocketLabel, verificationLabels, accountNote, formatEt } from "./duplicatesUtils";

// Possible duplicates (founder decision 2026-10-13, "3. OK"): pairs of
// accounts that may be one person, found by the hourly check (backend
// utils/dupeFlag, switch dupe_flag_v1). REVIEW ONLY: the two buttons record
// the founder's answer and nothing else. No delete here: an account is
// removed, if ever, from its own page.
const AccountCell = ({ account }) => {
  const note = accountNote(account);
  return (
    <div data-testid="dupe-account">
      {account.exists ? (
        <Link to={`/user/view-user/${account.id}`} className="fw-medium text-primary-600">
          {account.fullName || "No name yet"}
        </Link>
      ) : (
        <span className="fw-medium text-secondary-light">{account.id}</span>
      )}
      {account.email && <div className="text-sm text-secondary-light">{account.email}</div>}
      {account.createdAt && <div className="text-sm">Created {formatEt(account.createdAt)}</div>}
      <div className="text-sm">{verificationLabels(account).join(" · ")}</div>
      <div className="text-sm">{pocketLabel(account)}</div>
      {note && <span className="badge bg-neutral-200 text-neutral-600 mt-1">{note}</span>}
    </div>
  );
};

const PossibleDuplicatesLayer = () => {
  const [state, setState] = useState("new");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ new: 0, reviewed: 0, dismissed: 0 });
  const [enabled, setEnabled] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async (s) => {
    setLoading(true);
    setErr("");
    try {
      const res = await getPossibleDuplicates({ state: s });
      const data = res?.data?.data || {};
      setRows(data.rows || []);
      setCounts(data.counts || { new: 0, reviewed: 0, dismissed: 0 });
      setEnabled(data.enabled === true);
    } catch (e) {
      setErr("Could not load possible duplicates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(state);
  }, [state, load]);

  const answer = async (row, next) => {
    setBusyId(row.id);
    setErr("");
    try {
      await setDuplicateState(row.id, next);
      await load(state);
    } catch (e) {
      setErr("Could not save that. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  const scan = async () => {
    setScanning(true);
    setErr("");
    setNote("");
    try {
      const res = await scanDuplicates();
      const d = res?.data?.data || {};
      const parts = [];
      if (d.created) parts.push(`${d.created} new pair${d.created === 1 ? "" : "s"}`);
      if (d.reopened) parts.push(`${d.reopened} back in New on something new`);
      setNote(`Checked the last ${d.days} days: ${parts.length ? parts.join(", ") : "nothing new"}.`);
      await load(state);
    } catch (e) {
      setErr(e?.response?.data?.message || "Could not run the check.");
    } finally {
      setScanning(false);
    }
  };

  const countOf = (value) => (value === "all" ? counts.new + counts.reviewed + counts.dismissed : counts[value] || 0);

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 cz-toolbar">
        <div className="cz-topline">
          <div>
            <h6 className="mb-1 fw-semibold">Possible duplicates</h6>
            <p className="text-sm text-secondary-light mb-0" style={{ maxWidth: 680 }}>
              Pairs of accounts that may be one person, found by the hourly check. Review only: nothing here
              changes either account, and neither member is told. Open each account, then mark the pair{" "}
              <b>Reviewed</b> or <b>Not the same person</b>. A pair marked Not the same person is not listed as
              new again unless you move it back, or a later check finds something new about the two (for
              example the same phone number); then it is back in New with one more bell alert.
            </p>
          </div>
          <div className="cz-actions">
            <select className="form-select" value={state} onChange={(e) => setState(e.target.value)} aria-label="Show">
              {STATE_FILTERS.map((s) => (
                <option key={s.value} value={s.value}>
                  {`${s.label} (${countOf(s.value)})`}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary radius-8"
              onClick={scan}
              disabled={enabled !== true || scanning}
              title={enabled === false ? "The hourly check is OFF" : "Check the last 14 days now"}
            >
              {scanning ? "Checking…" : "Scan now"}
            </button>
          </div>
        </div>
        {enabled === false && (
          <div className="alert alert-warning mt-3 mb-0 text-sm" data-testid="dupe-off">
            The hourly check is OFF, so no new pairs are found. Turn on Possible Duplicate Accounts in Settings,
            under Doors and Pockets.
          </div>
        )}
        {note && <div className="text-sm text-secondary-light mt-2">{note}</div>}
      </div>

      <div className="card-body p-0">
        {err && (
          <div className="alert alert-danger m-3" role="alert">
            {err}
          </div>
        )}
        <div className="table-responsive">
          <table className="table bordered-table mb-0">
            <thead>
              <tr>
                <th>First account</th>
                <th>Second account</th>
                <th>What they share</th>
                <th>Found</th>
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid="dupe-row">
                  <td>
                    <AccountCell account={r.accounts[0]} />
                  </td>
                  <td>
                    <AccountCell account={r.accounts[1]} />
                  </td>
                  <td className="text-sm">
                    {(r.signals || []).map((s) => (
                      <div key={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</div>
                    ))}
                  </td>
                  <td className="text-sm">{formatEt(r.foundAt)}</td>
                  <td>
                    {r.state === "new" ? (
                      <div className="d-flex flex-column gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success radius-8"
                          disabled={busyId === r.id}
                          onClick={() => answer(r, "reviewed")}
                        >
                          Reviewed
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary radius-8"
                          disabled={busyId === r.id}
                          onClick={() => answer(r, "dismissed")}
                        >
                          Not the same person
                        </button>
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-1">
                        <span className="text-sm fw-medium">{stateLabel(r.state)}</span>
                        {r.reviewedAt && <span className="text-xs text-secondary-light">{formatEt(r.reviewedAt)}</span>}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-start"
                          disabled={busyId === r.id}
                          onClick={() => answer(r, "new")}
                        >
                          Back to new
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-4 text-secondary-light">
                    <Icon icon="mdi:check-circle-outline" className="me-1" />
                    {state === "new" ? "No pairs to review." : "No pairs here."}
                  </td>
                </tr>
              )}
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-4 text-secondary-light">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PossibleDuplicatesLayer;
